import React, { useEffect } from "react";
import { Card, Typography, Button, Box, CircularProgress } from "@mui/material";
import Ask7GIcon from "../../assets/navigations/Ask7g-icon.svg";
import Ask7GIconPurple from "../../assets/interaction-detail-page/selectedAsk7g_icon.svg";

const LOADING_MESSAGES = [
  "Crunching data magic…",
  "Analyzing your meeting…",
  "Tidying up your summary…",
  "Piecing insights together…",
  "Optimizing for clarity…",
  "Composing the essentials…",
  "Making it perfect…",
  "Scanning for highlights…",
  "Almost ready…",
  "Final touch-ups…",
];

interface PrepNotesCardProps {
  data: any;
  onGenerate: () => void;
  onClick: () => void;
  isGenerateLoading: boolean; 
  isInitialLoading: boolean; 
}

const PrepNotesCard: React.FC<PrepNotesCardProps> = ({
  data,
  onGenerate,
  onClick,
  isGenerateLoading,
  isInitialLoading,
}) => {

  const isCompleted = data && data.ai_analysis_status === "completed";
  const isInProgress = data && data.ai_analysis_status === "InProgress";
  const showEmptyState = !isCompleted && !isInProgress;
  const summary = isCompleted ? data.value?.executive_summary : "";
  const createdAt = data?.created_at;
  const date = isCompleted && createdAt
    ? new Date(createdAt).toLocaleDateString("en-GB")
    : "";
  const MAX_CHARS = 200;
  const isLongText = summary && summary.length > MAX_CHARS;
  const displayedText = isLongText ? summary.slice(0, MAX_CHARS) : summary;

  const cardStateClass = isCompleted
    ? "prep-notes-card--completed"
    : "prep-notes-card--default-center";

  const [dynamicMessage, setDynamicMessage] = React.useState("");

  useEffect(() => {
    if (isInitialLoading || isInProgress) {
      const msg = LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)];
      setDynamicMessage(msg);
    }
  }, [isInitialLoading, isInProgress]);

  if (!isCompleted && (isInitialLoading || isInProgress)) {
    return (
      <Card className="prep-notes-card prep-notes-card--default-center">
        <Box className="prep-notes-loading-container">
          <svg width="0" height="0" style={{ position: "absolute" }}>
            <defs>
              <linearGradient
                id="ask7gGradient"
                x1="2.52"
                y1="32.34"
                x2="33.6"
                y2="8.4"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0.216892" stopColor="#35013A" stopOpacity="0.06" />
                <stop offset="0.995265" stopColor="#7F018B" />
              </linearGradient>
            </defs>
          </svg>

          <Box className="prep-notes-progress-box">
            <CircularProgress
              size={50}
              thickness={3}
              sx={{
                "& .MuiCircularProgress-circle": {
                  stroke: "url(#ask7gGradient)",
                },
              }}
            />
            <Box className="prep-notes-progress-icon-box">
              <img
                src={Ask7GIconPurple}
                alt="Ask7G"
                className="prep-notes-progress-icon"
              />
            </Box>
          </Box>
  
          <Typography
            className="prep-notes-generating-text"
            sx={{ marginTop: "16px" }}
          >
            {dynamicMessage}
          </Typography>
        </Box>
      </Card>
    );
  }  

  return (
    <Card
      className={`prep-notes-card ${cardStateClass}`}
      onClick={isCompleted ? onClick : undefined}
    >

      {isCompleted && (
        <>
          <Box className="prep-notes-completed-header">
            <Typography className="prep-notes-date-text">
              {date}
            </Typography>
            <Typography className="prep-notes-powered-by">
              Powered by 7G
            </Typography>
          </Box>
          <Box className="prep-notes-divider" />
          <Box className="prep-notes-summary-content">
            <Typography
              onClick={onClick}
              className="prep-notes-summary-text"
            >
              {displayedText}
              {isLongText && (
                <Box
                  component="span"
                  className="prep-notes-see-more"
                >
                  ...see more
                </Box>
              )}
            </Typography>
          </Box>
        </>
      )}

      {showEmptyState && (
        <>
          <Box className="prep-notes-empty-state-text-box">
            <Typography className="prep-notes-empty-state-text">
              Quickly generate AI-powered prep notes summarizing agenda,
              participants, and key details for your upcoming interaction.
            </Typography>
          </Box>

          {/* Button is only shown in the Empty State */}
          <Button
            variant="contained"
            className="prep-notes-generate-btn"
            onClick={onGenerate}
            disabled={isGenerateLoading}
            sx={{
              opacity: isGenerateLoading ? 0.4 : 1,
              transition: "opacity 0.3s ease",
              marginTop: "24px",
            }}
          >
            <img
              src={Ask7GIcon}
              alt="Ask7G"
              className="prep-notes-generate-btn-icon"
            />
            Generate Prep Note
          </Button>
        </>
      )}
    </Card>
  );
};

export default PrepNotesCard;
